import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MAX_ATTACHMENT_BYTES,
  attachmentsApi,
  uploadToUrl,
  attachmentContentType,
  validateAttachmentFile,
  type AttachmentEntityKind,
  type AttachmentKind,
  type AttachmentView,
} from "../api/attachments";
import type { ApiError } from "../api/client";
import { formatApiError } from "./apiError";
import { useToast } from "../components/Toast";

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

export type UploadStatus = "queued" | "presigning" | "uploading" | "confirming" | "error";

export type UploadRow = {
  key: string;
  file: File;
  kind: AttachmentKind;
  status: UploadStatus;
  error: string | null;
  /** False when the file failed the client-side check — nothing to retry. */
  retryable: boolean;
};

/** Stage → bar width. `fetch` gives no byte progress, so the bar advances per phase. */
export const UPLOAD_STAGE_PERCENT: Record<UploadStatus, number> = {
  queued: 5,
  presigning: 20,
  uploading: 60,
  confirming: 90,
  error: 100,
};

export function uploadStageLabel(t: Translate, status: UploadStatus): string {
  switch (status) {
    case "queued": return t("attachments.stage.queued", "Čeka…");
    case "presigning": return t("attachments.stage.presigning", "Priprema…");
    case "uploading": return t("attachments.stage.uploading", "Slanje…");
    case "confirming": return t("attachments.stage.confirming", "Potvrda…");
    default: return "";
  }
}

export function uploadErrorMessage(t: Translate, err: unknown): string {
  const ae = err as Partial<ApiError> & { message?: string };
  if (ae.code === "UPLOAD_INVALID") return t("attachments.error.uploadInvalid", "Sadržaj fajla ne odgovara njegovom tipu.");
  if (ae.code === "VALIDATION") return formatApiError(ae as ApiError);
  if (ae.status === 404) return t("attachments.error.entityMissing", "Entitet više ne postoji.");
  return ae.message || t("attachments.error.upload", "Slanje nije uspelo.");
}

export function fileProblemMessage(t: Translate, problem: NonNullable<ReturnType<typeof validateAttachmentFile>>): string {
  void problem; // TOO_LARGE is the only problem left — any file type is accepted
  return t("attachments.error.tooLarge", "Fajl je veći od {{max}} MB.", { max: Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024) });
}

export type AttachmentUpload = {
  /** In-flight and failed rows; a row disappears once its file is confirmed. */
  uploads: UploadRow[];
  /** Validates, queues and runs the files one by one (the server numbers the codes in pick order). */
  addFiles: (files: File[], kind: AttachmentKind) => Promise<void>;
  retry: (row: UploadRow) => Promise<void>;
  dismiss: (key: string) => void;
  /** Drops every row (entity change). */
  reset: () => void;
};

/**
 * The presign → raw PUT → confirm flow of contract sprint 4 §3, shared by the
 * attachments panel and the layout background picker. Each file gets a progress
 * row; on success `onUploaded` receives the READY view and the row goes away, on
 * failure the row stays red with a retry. Errors also go to a toast.
 */
export function useAttachmentUpload(
  entityKind: AttachmentEntityKind,
  entityId: string,
  onUploaded: (view: AttachmentView) => void,
): AttachmentUpload {
  const { t } = useTranslation();
  const toast = useToast();
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const seq = useRef(0);
  const onUploadedRef = useRef(onUploaded);
  useEffect(() => { onUploadedRef.current = onUploaded; }, [onUploaded]);

  const patch = useCallback((key: string, changes: Partial<UploadRow>) => {
    setUploads((prev) => prev.map((r) => (r.key === key ? { ...r, ...changes } : r)));
  }, []);

  const run = useCallback(async (row: UploadRow) => {
    patch(row.key, { status: "presigning", error: null });
    try {
      const presigned = await attachmentsApi.presign({
        entityKind,
        entityId,
        kind: row.kind,
        filename: row.file.name,
        contentType: attachmentContentType(row.file),
        sizeBytes: row.file.size,
      });
      patch(row.key, { status: "uploading" });
      await uploadToUrl(row.file, presigned.uploadUrl);
      patch(row.key, { status: "confirming" });
      const view = await attachmentsApi.confirm(presigned.attachmentId);
      setUploads((prev) => prev.filter((r) => r.key !== row.key));
      onUploadedRef.current(view);
    } catch (err) {
      const message = uploadErrorMessage(t, err);
      patch(row.key, { status: "error", error: message });
      toast.error(`${row.file.name}: ${message}`);
    }
  }, [entityKind, entityId, patch, t, toast]);

  const addFiles = useCallback(async (files: File[], kind: AttachmentKind) => {
    if (files.length === 0) return;
    const rows: UploadRow[] = files.map((file) => {
      const problem = validateAttachmentFile(file);
      return {
        key: `u${++seq.current}`,
        file,
        kind,
        status: problem ? "error" : "queued",
        error: problem ? fileProblemMessage(t, problem) : null,
        retryable: !problem,
      };
    });
    setUploads((prev) => [...prev, ...rows]);
    rows.filter((r) => r.status === "error").forEach((r) => toast.error(`${r.file.name}: ${r.error}`));
    for (const row of rows) {
      if (row.status === "queued") await run(row);
    }
  }, [run, t, toast]);

  const dismiss = useCallback((key: string) => {
    setUploads((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const reset = useCallback(() => setUploads([]), []);

  return { uploads, addFiles, retry: run, dismiss, reset };
}
